import { IAppContext } from 'App/AppContext';
import {
  CopilotConversation,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import { SharepointLibraries, SharepointReportsAndPublishing } from 'models/setting';
import { Dispatch, SetStateAction } from 'react';
import { apiSetSetting } from 'services/Api/settingService';
import { apiRequest } from 'services/Auth/authConfig';

// A Document item never renders in the chat (CopilotChatItem has no case for it). A held document is named by
// the attached-document row, not counted here.
export const hasChatItems = (conversation: CopilotConversation | undefined): boolean =>
  (conversation?.thread ?? []).some((item) => item.type !== CopilotConversionItemType.Document);

export const mergeAdaptiveInputs = (
  currentConversation: CopilotConversation | undefined,
  incomingConversation: CopilotConversation,
): CopilotConversation => {
  if (!currentConversation) return incomingConversation;

  const incoming = incomingConversation.clone();
  const currentByBlockId = new Map<string, Record<string, string>>();

  for (const currentItem of currentConversation.thread) {
    for (const block of currentItem.contentBlocks ?? []) {
      if (block.id && block.adaptiveInputs) {
        currentByBlockId.set(block.id, block.adaptiveInputs);
      }
    }
  }

  for (const incomingItem of incoming.thread) {
    for (const block of incomingItem.contentBlocks ?? []) {
      if (block.adaptiveInputs || !block.id) continue;

      const existingByBlockId = currentByBlockId.get(block.id);
      if (existingByBlockId) {
        block.adaptiveInputs = { ...existingByBlockId };
      }
    }
  }

  return incoming;
};

export const setSharePointLibraryFromConsent = async (
  appContext: IAppContext,
  checked: boolean,
  _getPermissions: boolean,
) => {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  const setting = await apiSetSetting(SharepointLibraries, checked, accessToken);
  if (setting.settingValue) {
    appContext.globalDataCache.settings.set(SharepointLibraries, checked);
  }
};

export const setSharePointReportFromConsent = async (
  appContext: IAppContext,
  checked: boolean,
  _getPermissions: boolean,
) => {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  const setting = await apiSetSetting(SharepointReportsAndPublishing, checked, accessToken);
  if (setting.settingValue) {
    appContext.globalDataCache.settings.set(SharepointReportsAndPublishing, checked);
  }
};

export const openSharepointConsentConversation = (
  setConversation: Dispatch<SetStateAction<CopilotConversation | undefined>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  startProcess: () => Promise<void>,
) => {
  const conversation = new CopilotConversation();
  const message = new CopilotConversationItem();
  message.type = CopilotConversionItemType.SharepointIntegrations;
  message.action = startProcess;
  // Re-opened after the consent redirect: when the integrations turn out complete, start right away.
  message.actionOnMount = true;
  conversation.thread.push(message);
  setConversation(conversation);
  setIsLoading(false);
};
